import withRouter from "Components/Common/withRouter"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { createSelector } from "reselect"
import { ColumnDef, Row } from "@tanstack/react-table"
import useDataTable from "hooks/useDataTable"
import React from "react"
import Opportunity from "models/opportunity"
import { PAGE_KEY, LIMIT_KEY } from "models/queryable"
import {
    getOpportunityForecast,
    updateOpportunityFinance,
    getOpportunitySearchFields,
} from "slices/thunk"
import { downloadOpportunityForecastCSV } from "slices/business-development/opportunity-forecast/thunk"
import Branch from "models/branch"
import Client from "models/client"
import Employee from "models/employee"
import DataTableTemplate from "Components/templates/data-table-template"
import SearchOpportunitiesForm from "Components/organisms/forms/search-opportunities-form"
import { formatCurrency } from "utils/tools"
import { mergeOpportunityFinacnes, OpportunityForecastSliceState, setQuery as setOpportunityForecastQuery } from "slices/business-development/opportunity-forecast/reducer"
import FiscalCalendar, { DEFAULT_FISCAL_START } from "utils/fiscal-calendar"
import SearchSelect from "Components/molecule/inputs/search-select"
import OpportunityFinance from "models/opportunity-finance"
import PrimaryButton from "Components/atoms/buttons/primary-button"
import SecondaryButton from "Components/atoms/buttons/secondary-button"
import CurrencyInput from "Components/molecule/inputs/currency-input"
import { useDiffStore } from "hooks/useInputDiffStore"
import useManual from "hooks/useManual"
import { toDepartmentOptions } from "utils/department-options"
import ActionButton from "Components/atoms/buttons/action-button"
import useFiscalYearOptions from "hooks/useFiscalYearOptions"
import { ChevronDownIcon, ChevronRightIcon, HelpIcon } from "Components/atoms/icons"
import { toast } from "react-toastify"
import { getStageRank, getStageProbability } from "utils/opportunity-stage"

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const OPPORTUNITY_FORECAST_CHANGES = "opportunity_forecast_changes"
const DIRTY_INPUT_CLASS = "forecast-input--dirty"
const FORECAST_CONTROLS_ID = "opp-forecast-controls"
const DEFAULT_FISCAL_YEAR = FiscalCalendar.lastMonthFiscalYear()
const EXCLUDED_STAGES = ["Won", "Lost", "Process Canceled"]

const SUBMITTED_RFQ_COLOR = "#fce49c"        // yellow, lightest
const SUBMITTED_PROPOSAL_COLOR = "#fbd051"   // yellow, mid
const IN_NEGOTIATION_COLOR = "#f9bc06"       // yellow, darkest
const ENGAGED_COLOR = "#485fab"         // #485fab
const IDENTIFIED_COLOR = "#516bdd"      // #516bdd

const getStageBackgroundColor = (stageName?: string): string => {
    if (!stageName) return "#2c3e50"

    const normalized = stageName.toLowerCase().trim()


    if (normalized.startsWith("identified")) return IDENTIFIED_COLOR
    if (normalized.startsWith("engaged")) return ENGAGED_COLOR
    if (normalized.startsWith("submitted rfq/msa")) return SUBMITTED_RFQ_COLOR
    if (normalized.startsWith("submitted proposal")) return SUBMITTED_PROPOSAL_COLOR
    if (normalized.startsWith("negotiation")) return IN_NEGOTIATION_COLOR

    return "#2c3e50"
}


const getStageHeaderTextColor = (stageName?: string): string => "black"

const stageHeaderTopStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    color: 'black',
    fontWeight: 600,
    fontSize: "0.9em",
}

const stageHeaderBotStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    color: 'black',
    fontSize: "0.75em",
}


const oppTopStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
}

const oppBotStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    fontSize: "0.75em",
}

// ---------------------------------------------------------------------------
// Redux selectors
// ---------------------------------------------------------------------------

const selectOpportunitiesForecast = createSelector(
    (state: any) => state.OpportunityForecast,
    ({
        projectManagers,
        branches,
        departments,
        clients,
        opportunities,
        opportunityCount,
        searchFieldsLoaded,
        query,
    }: OpportunityForecastSliceState) => ({
        projectManagers,
        branches,
        departments,
        clients,
        opportunities,
        opportunityCount,
        searchFieldsLoaded,
        query,
    })
)


// ---------------------------------------------------------------------------
// Types for grouped rows
// ---------------------------------------------------------------------------

type StageHeaderRow = {
    __rowType: "stage-header"
    stageId: string
    stageName: string
    probability: number
    opportunityIds: string[]
}

type OpportunityRow = Opportunity & { __rowType: "opportunity" }

type TableRow = StageHeaderRow | OpportunityRow

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Computes the forecast total for a single opportunity.
 * Sums all opportunityFinances records, substituting any staged changes.
 */
const computeOppForecastTotal = (
    opp: Opportunity,
    storedChanges: Record<string, number>
): number => {
    // Build a map of all finance records keyed by "oppId+yearId+periodId"
    const financeMap: Record<string, number> = {}
    if (opp.opportunityFinances) {
        for (const f of opp.opportunityFinances) {
            const key = `${opp.id}+${f.fiscalPeriod}`
            financeMap[key] = Number(f.forecastAmount ?? 0)
        }
    }

    // Apply staged changes (may add new keys or override existing ones)
    for (const [key, value] of Object.entries(storedChanges)) {
        if (key.startsWith(opp.id + "+")) {
            financeMap[key] = value
        }
    }

    return Object.values(financeMap).reduce((sum, v) => sum + v, 0)
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

const OpportunityForecastPage = (props: any) => {
    document.title = "Opportunity Forecast | IbexIQ"

    const [fiscalYear, setFiscalYear] = useState<number>(DEFAULT_FISCAL_YEAR)
    const { options: fiscalYearOptions, defaultYear, isLoading: isLoadingFiscalYears } = useFiscalYearOptions()

    // If the current fiscal year is hidden, move to the newest visible year (only if the user hasn't picked one).
    useEffect(() => {
        if (!isLoadingFiscalYears && defaultYear !== DEFAULT_FISCAL_YEAR && fiscalYear === DEFAULT_FISCAL_YEAR) {
            setFiscalYear(defaultYear)
        }
    }, [isLoadingFiscalYears, defaultYear])
    const [isLoadingOpportunities, setIsLoadingOpportunities] = useState<boolean>(true)
    const [isSavingForecast, setIsSavingForecast] = useState<boolean>(false)
    const [isExporting, setIsExporting] = useState(false)
    const [collapsedStages, setCollapsedStages] = useState<Set<string>>(new Set())
    const { handleOpenManual } = useManual("business-development/opportunity-forecast");

    const dispatch: any = useDispatch()

    const { addChange, removeChange, getStoredChanges, getValue, commitChanges } =
        useDiffStore<number>({
            storageKey: OPPORTUNITY_FORECAST_CHANGES,
            dirtyClass: DIRTY_INPUT_CLASS,
        })

    const calendar = new FiscalCalendar(DEFAULT_FISCAL_START)

    const {
        opportunities,
        opportunityCount,
        branches,
        departments,
        projectManagers,
        clients,
        searchFieldsLoaded,
        query,
    } = useSelector(selectOpportunitiesForecast)


    // -----------------------------------------------------------------------
    // Derived data
    // -----------------------------------------------------------------------

    const filteredOpportunities: Opportunity[] = useMemo(() => {
        return opportunities
            ? opportunities.filter((o: Opportunity) =>
                o.stage ? !EXCLUDED_STAGES.includes(o.stage.description) : false
            )
            : []
    }, [opportunities])

    const tableRows: TableRow[] = useMemo(() => {
        const stageMap = new Map<
            string,
            { stageName: string; stageId: string; opps: Opportunity[] }
        >()

        for (const opp of filteredOpportunities) {
            const sid = opp.stage?.id ?? "unknown"
            if (!stageMap.has(sid)) {
                stageMap.set(sid, {
                    stageId: sid,
                    stageName: opp.stage?.description ?? "Unknown",
                    opps: [],
                })
            }
            stageMap.get(sid)!.opps.push(opp)
        }

        const rows: TableRow[] = []
        const sortedStages = Array.from(stageMap.entries()).sort(
            ([, a], [, b]) => getStageRank(a.stageName) - getStageRank(b.stageName)
        )

        for (const [stageId, { stageName, opps }] of sortedStages) {
            const probability = getStageProbability(stageName)
            const isCollapsed = collapsedStages.has(stageId)

            rows.push({
                __rowType: "stage-header",
                stageId,
                stageName,
                probability,
                opportunityIds: opps.map((o) => o.id),
            } as StageHeaderRow)

            if (!isCollapsed) {
                for (const opp of opps) {
                    rows.push({ ...opp, __rowType: "opportunity" } as OpportunityRow)
                }
            }
        }

        return rows
    }, [filteredOpportunities, collapsedStages])

    // -----------------------------------------------------------------------
    // Init
    // -----------------------------------------------------------------------

    const initializePage = async () => {
        if (!searchFieldsLoaded) {
            await dispatch(getOpportunitySearchFields())
        }
        if (opportunities.length === 0) {
            await dispatch(getOpportunityForecast(query))
        }
        setIsLoadingOpportunities(false)
    }

    useEffect(() => {
        initializePage()
        localStorage.removeItem(OPPORTUNITY_FORECAST_CHANGES)
        return () => localStorage.removeItem(OPPORTUNITY_FORECAST_CHANGES)
    }, [])

    // Recalculate DOM totals AND stage subtotals on init and whenever data/year changes
    useEffect(() => {
        if (filteredOpportunities.length === 0) return
        const timeout = setTimeout(() => {
            const uniqueStageIds = [
                ...new Set(
                    filteredOpportunities.map((o) => o.stage?.id).filter(Boolean)
                ),
            ]
            uniqueStageIds.forEach((sid) => calculateStageSubtotals(sid!))
            calculateTotalRow()
        })
        return () => clearTimeout(timeout)
    }, [fiscalYear, filteredOpportunities, collapsedStages])

    // -----------------------------------------------------------------------
    // DOM totals
    // -----------------------------------------------------------------------
    const calculateTotalRow = () => {
        const uniqueStageIds = [
            ...new Set(filteredOpportunities.map((o) => o.stage?.id).filter(Boolean)),
        ]

        for (let p = 1; p <= 12; p++) {
            const summaryCell = document.querySelector(`.period-${p}-${fiscalYear}-summary`)
            const weightedSummaryCell = document.querySelector(
                `.period-${p}-${fiscalYear}-weighted-summary`
            )

            let rawTotal = 0
            let weightedTotal = 0
            uniqueStageIds.forEach((stageId) => {
                const rawCell = document.querySelector(
                    `.stage-subtotal-${stageId}-period-${p}-${fiscalYear}`
                )
                const weightedCell = document.querySelector(
                    `.stage-subtotal-${stageId}-period-${p}-${fiscalYear}-weighted`
                )
                rawTotal += Number(rawCell?.textContent?.replace(/[^0-9.-]/g, "") ?? "0")
                weightedTotal += Number(weightedCell?.textContent?.replace(/[^0-9.-]/g, "") ?? "0")
            })

            if (summaryCell) summaryCell.innerHTML = formatCurrency(rawTotal)
            if (weightedSummaryCell) weightedSummaryCell.innerHTML = formatCurrency(weightedTotal)
        }

        const totalSummary = document.querySelector(".opp-total-summary")
        const weightedSummary = document.querySelector(".opp-weighted-total-summary")

        const grandTotal = Array.from(document.querySelectorAll(".stage-value"))
            .reduce((sum, el) => sum + Number(el.textContent?.replace(/[^0-9.-]/g, "") ?? "0"), 0)

        const grandWeighted = Array.from(document.querySelectorAll(".stage-value-weighted"))
            .reduce((sum, el) => sum + Number(el.textContent?.replace(/[^0-9.-]/g, "") ?? "0"), 0)

        if (totalSummary) totalSummary.innerHTML = formatCurrency(grandTotal)
        if (weightedSummary) weightedSummary.innerHTML = formatCurrency(grandWeighted)

        // Forecast total grand totals
        updateForecastTotalGrandTotals()
    }

    // -----------------------------------------------------------------------
    // Forecast total DOM helpers
    // -----------------------------------------------------------------------

    /**
     * Recomputes and writes the forecast total for a single opportunity row into the DOM.
     */
    const updateOppForecastTotalCell = (
        opportunityId: string,
        storedChanges: Record<string, number>
    ) => {
        const opp = filteredOpportunities.find((o) => o.id === opportunityId)
        if (!opp) return

        const raw = computeOppForecastTotal(opp, storedChanges)
        const prob = getStageProbability(opp.stage?.description ?? "")
        const weighted = raw * prob

        const rawCell = document.querySelector(`.forecast-total-raw-${opportunityId}`)
        const weightedCell = document.querySelector(`.forecast-total-weighted-${opportunityId}`)

        if (rawCell) rawCell.innerHTML = formatCurrency(raw)
        if (weightedCell) weightedCell.innerHTML = formatCurrency(weighted)
    }

    /**
     * Recomputes the stage subtotal for the forecast total column.
     */
    const updateStageForecastTotalSubtotal = (
        stageId: string,
        storedChanges: Record<string, number>
    ) => {
        const stageOpps = filteredOpportunities.filter(
            (o) => o.stage?.id === stageId
        )
        const stageName = stageOpps[0]?.stage?.description ?? ""
        const prob = getStageProbability(stageName)

        let rawTotal = 0
        for (const opp of stageOpps) {
            rawTotal += computeOppForecastTotal(opp, storedChanges)
        }

        const rawCell = document.querySelector(`.stage-forecast-total-${stageId}`)
        const weightedCell = document.querySelector(`.stage-forecast-total-weighted-${stageId}`)

        if (rawCell) rawCell.innerHTML = formatCurrency(rawTotal)
        if (weightedCell) weightedCell.innerHTML = formatCurrency(rawTotal * prob)
    }

    /**
     * Recomputes the grand total footer for the forecast total column.
     */
    const updateForecastTotalGrandTotals = (storedChanges?: Record<string, number>) => {
        const uniqueStageIds = [
            ...new Set(filteredOpportunities.map((o) => o.stage?.id).filter(Boolean)),
        ]

        let grandRaw = 0
        let grandWeighted = 0

        for (let i = 0; i <= uniqueStageIds.length; i++) {
            const rawCell = document.querySelector(
                `.stage-forecast-total-${uniqueStageIds[i]}`
            )
            const weightedCell = document.querySelector(
                `.stage-forecast-total-weighted-${uniqueStageIds[i]}`
            )
            grandRaw += Number(rawCell?.textContent?.replace(/[^0-9.-]/g, "") ?? "0")
            grandWeighted += Number(weightedCell?.textContent?.replace(/[^0-9.-]/g, "") ?? "0")
        }

        const rawSummary = document.querySelector(".forecast-total-grand-summary")
        const weightedSummary = document.querySelector(".forecast-total-grand-weighted-summary")

        if (rawSummary) rawSummary.innerHTML = formatCurrency(grandRaw)
        if (weightedSummary) weightedSummary.innerHTML = formatCurrency(grandWeighted)
    }

    // -----------------------------------------------------------------------
    // Stage subtotals
    // -----------------------------------------------------------------------

    const calculateStageSubtotals = (stageId: string, storedChanges: any = {}) => {
        const stageOpps = filteredOpportunities.filter(
            (o) => o.stage?.id === stageId
        )
        const stageName = stageOpps[0]?.stage?.description ?? ""
        const prob = getStageProbability(stageName)

        for (let p = 1; p <= 12; p++) {
            let periodTotal = 0
            stageOpps.forEach((opp) => {
                const key = `${opp.id}+${fiscalYear * 100 + p}`
                if (storedChanges[key] !== undefined) {
                    periodTotal += storedChanges[key]
                } else {
                    const pF = opp.opportunityFinances?.find(
                        (f) => f.fiscalPeriod === fiscalYear * 100 + p
                    )
                    periodTotal += Number(pF?.forecastAmount ?? 0)
                }
            })

            const rawCell = document.querySelector(
                `.stage-subtotal-${stageId}-period-${p}-${fiscalYear}`
            )
            const weightedCell = document.querySelector(
                `.stage-subtotal-${stageId}-period-${p}-${fiscalYear}-weighted`
            )

            if (rawCell) rawCell.innerHTML = formatCurrency(periodTotal)
            if (weightedCell) weightedCell.innerHTML = formatCurrency(periodTotal * prob)
        }

        // Also update the forecast total subtotal for this stage
        updateStageForecastTotalSubtotal(stageId, storedChanges)
    }

    // -----------------------------------------------------------------------
    // Change handlers
    // -----------------------------------------------------------------------

    const handleShowTableControls = () => {
        const el = document.getElementById(FORECAST_CONTROLS_ID)
        if (el) el.style.display = "block"
    }

    const handleHideTableControls = () => {
        const el = document.getElementById(FORECAST_CONTROLS_ID)
        if (el) el.style.display = "none"
    }

    const handleAddChange = (
        opportunityId: string,
        fiscalPeriod: number,
        amount: number,
        stageId: string
    ) => {
        const inputId = `${opportunityId}+${fiscalPeriod}`
        const storedChanges = addChange(inputId, amount)
        handleShowTableControls()
        calculateStageSubtotals(stageId, storedChanges)
        calculateTotalRow()
        updateOppForecastTotalCell(opportunityId, storedChanges)
        updateForecastTotalGrandTotals(storedChanges)
    }

    const handleRemoveChange = (
        opportunityId: string,
        fiscalPeriod: number,
        stageId: string
    ) => {
        const key = `${opportunityId}+${fiscalPeriod}`
        let newValue = 0
        const opp = filteredOpportunities.find((o) => o.id === opportunityId)
        if (opp) {
            const pF = opp.opportunityFinances?.find(
                (f) => f.fiscalPeriod === fiscalPeriod
            )
            newValue = pF ? Number(pF.forecastAmount) : 0
        }

        const updatedChanges = removeChange(key, "$" + newValue)
        if (Object.keys(updatedChanges).length === 0) handleHideTableControls()

        calculateStageSubtotals(stageId, updatedChanges)
        calculateTotalRow()
        updateOppForecastTotalCell(opportunityId, updatedChanges)
        updateForecastTotalGrandTotals(updatedChanges)
    }

    const handleReset = () => {
        const storedChanges = getStoredChanges()
        const keys = Object.keys(storedChanges)
        for (const key of keys) {
            const [oppId, fiscalPeriodStr] = key.split("+")
            const opp = filteredOpportunities.find((o) => o.id === oppId)
            let newValue = 0
            if (opp) {
                const pF = opp.opportunityFinances?.find(
                    (f) => f.fiscalPeriod === parseFloat(fiscalPeriodStr)
                )
                newValue = pF ? Number(pF.forecastAmount) : 0
            }
            removeChange(key, "$" + newValue)
        }
        handleHideTableControls()
        calculateTotalRow()

        const uniqueStageIds = [
            ...new Set(
                filteredOpportunities.map((o) => o.stage?.id).filter(Boolean)
            ),
        ]
        uniqueStageIds.forEach((sid) => calculateStageSubtotals(sid!))

        // Reset all opp forecast total cells and grand total
        filteredOpportunities.forEach((opp) =>
            updateOppForecastTotalCell(opp.id, {})
        )
        updateForecastTotalGrandTotals({})
    }

    const handleSave = async () => {
        const storedChanges = getStoredChanges()
        setIsSavingForecast(true)

        const finances: OpportunityFinance[] = Object.keys(storedChanges).map((key) => {
            const [opportunityId, fiscalPeriodStr] = key.split("+")
            return {
                opportunityId,
                fiscalPeriod: parseFloat(fiscalPeriodStr),
                forecastAmount: storedChanges[key],
            } as unknown as OpportunityFinance
        })

        const res = await updateOpportunityFinance(finances)
        if (res) {
            // Merge changes with cache
            dispatch(mergeOpportunityFinacnes(res));
            commitChanges()
            handleHideTableControls()
        }

        setIsSavingForecast(false)
    }

    // -----------------------------------------------------------------------
    // Query handlers
    // -----------------------------------------------------------------------

    const handleChangeQuery = (key: string, value: any) => {
        const q = { ...query }
        if (key !== PAGE_KEY && key !== LIMIT_KEY) {
            q.page = 0
            q.limit = Number.MAX_SAFE_INTEGER
        }
        q[key] = value
        dispatch(setOpportunityForecastQuery(q))
    }

    const handleSearch = useCallback(async () => {
        setIsLoadingOpportunities(true)
        await dispatch(getOpportunityForecast(query))
        setIsLoadingOpportunities(false)
    }, [query])

    const handleClearSearch = useCallback(() => {
        dispatch(setOpportunityForecastQuery({
            page: 0,
            limit: Number.MAX_SAFE_INTEGER,
            branchIds: [],
            ownerIds: [],
            departmentIds: [],
            clientIds: [],
        }))
    }, [dispatch])

    const handleChangeFiscalYear = (year: number) => setFiscalYear(year)

    const handleExportData = () => {
        setIsExporting(true)
        try {
            downloadOpportunityForecastCSV(filteredOpportunities, fiscalYear, query, branches, projectManagers, departments, clients)
        } finally {
            setIsExporting(false)
        }
    }

    const toggleStage = (stageId: string) => {
        setCollapsedStages((prev) => {
            const next = new Set(prev)
            if (next.has(stageId)) next.delete(stageId)
            else next.add(stageId)
            return next
        })
    }


    const handleNavigateToAcumatica = (project: Opportunity) => {
        if (!process.env.REACT_APP_ACUMATICA_PROJECT_URL) {
            toast.error("Could not find Acumatica URL.")
            return
        }

        const url = process.env.REACT_APP_ACUMATICA_OPPORTUNITY_URL + encodeURIComponent(project.id)
        window.open(url, "_blank")
    }



    // -----------------------------------------------------------------------
    // Columns
    // -----------------------------------------------------------------------

    const buildPeriodCell = (PERIOD: number) => ({
        size: 90,
        header: `${calendar.fiscalToLabel(fiscalYear, PERIOD)}`,
        id: `${PERIOD}-${fiscalYear}`,
        meta: {
            getHeaderStyle: () => ({ textAlign: "right", padding: "8px 12px" }),
            getCellStyle: (cell: any) => {
                const data = cell.row.original as TableRow
                if (data.__rowType === "stage-header") {
                    return {
                        padding: 0,
                        textAlign: "right" as const,
                        backgroundColor: getStageBackgroundColor((data as StageHeaderRow).stageName),
                    }
                }
                return { padding: "0px", textAlign: "right" as const }
            },
            getFooterStyle: () => ({ padding: 0, backgroundColor: "darkgreen", }),
        },
        footer: () => {
            if (table.getRowModel().rows.length === 0) return null
            return (
                <div style={{ backgroundColor: "darkgreen", padding: "10px 12px" }}>
                    <div
                        className={`period-${PERIOD}-${fiscalYear}-summary`}
                        style={{ ...stageHeaderTopStyle, color: 'white' }}
                    />
                    <div
                        title="Weighted"
                        className={`period-${PERIOD}-${fiscalYear}-weighted-summary`}
                        style={{ ...stageHeaderBotStyle, color: 'white' }}
                    />
                </div>
            )
        },
        cell: ({ row }: { row: Row<TableRow> }) => {
            const data = row.original

            if (data.__rowType === "stage-header") {
                const sh = data as StageHeaderRow
                return (
                    <div style={{
                        padding: '8px 12px'
                    }}>
                        <div
                            className={`stage-subtotal-${sh.stageId}-period-${PERIOD}-${fiscalYear}`}
                            style={{
                                ...stageHeaderTopStyle,
                                color: getStageHeaderTextColor(data.stageName)
                            }}
                        />
                        <div
                            title={`Weighted (${sh.probability})`}
                            className={`stage-subtotal-${sh.stageId}-period-${PERIOD}-${fiscalYear}-weighted`}
                            style={{ ...stageHeaderBotStyle }}
                        />
                    </div>
                )
            }

            const opp = data as OpportunityRow
            const fiscalPeriod = fiscalYear * 100 + PERIOD
            const inputId = `${opp.id}+${fiscalPeriod}`
            const pF = opp.opportunityFinances?.find(
                (p) => p.fiscalPeriod === fiscalPeriod
            )
            const changedValue = getValue(inputId)
            const value =
                changedValue !== undefined ? changedValue : Number(pF?.forecastAmount ?? 0)

            return (
                <CurrencyInput
                    defaultValue={"$" + value}
                    id={inputId}
                    data-stage={opp.stage?.description ?? ""}
                    inputClassName={`period-${PERIOD}-${fiscalYear} forecast-input ${changedValue !== undefined ? DIRTY_INPUT_CLASS : ""}`}
                    onCommit={(newValue) => {
                        if (newValue === Number(pF?.forecastAmount ?? 0)) {
                            handleRemoveChange(opp.id, fiscalPeriod, opp.stageId)
                        } else {
                            handleAddChange(opp.id, fiscalPeriod, newValue ?? 0, opp.stageId)
                        }
                    }}
                />
            )
        },
    })

    const columns = React.useMemo<ColumnDef<TableRow>[]>(
        () => [
            // ---- Opportunity / Stage name column ----
            {
                header: "Opportunity",
                accessorKey: "description",
                size: 300,
                footer: ({ table }) => {
                    if (table.getRowModel().rows.length === 0) return null
                    return (
                        <div style={{
                            padding: "10px 12px"
                        }}>
                            <div
                                style={{
                                    ...stageHeaderTopStyle,
                                    justifyContent: "flex-start",
                                    fontWeight: 700,
                                    fontSize: "0.95em",
                                    color: 'white'
                                }}
                            >
                                Grand Total
                            </div>
                            <div
                                style={{
                                    ...stageHeaderBotStyle,
                                    justifyContent: "flex-start",
                                    color: 'white'
                                }}
                            >
                                Weighted
                            </div>
                        </div>
                    )
                },
                meta: {
                    getCellStyle: (cell: any) => {
                        const data = cell.row.original as TableRow
                        if (data.__rowType === "stage-header") {
                            return {
                                padding: "0 12px",
                                backgroundColor: getStageBackgroundColor((data as StageHeaderRow).stageName),
                                color: getStageHeaderTextColor((data as StageHeaderRow).stageName),
                                cursor: "pointer",
                                userSelect: "none" as const,
                                fontWeight: 700,
                                fontSize: "0.9em",
                                letterSpacing: "0.02em",
                            }
                        }
                        return { padding: "0 12px" }
                    },
                    getFooterStyle: () => ({ padding: 0, backgroundColor: "darkgreen" }),
                },
                cell: ({ row }) => {
                    const data = row.original

                    if (data.__rowType === "stage-header") {
                        const sh = data as StageHeaderRow
                        const isCollapsed = collapsedStages.has(sh.stageId)
                        return (
                            <div
                                onClick={() => toggleStage(sh.stageId)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                }}
                            >
                                <span style={{ fontSize: "0.75em", flexShrink: 0 }}>
                                    {isCollapsed ? <ChevronRightIcon className="font-size-18" /> : <ChevronDownIcon className="font-size-18" />}
                                </span>
                                <span style={{ flexShrink: 0 }}>{sh.stageName}</span>
                                <span
                                    style={{
                                        backgroundColor: "#1a252f",
                                        padding: '3px',
                                        borderRadius: "10px",
                                        fontSize: "0.75em",
                                        fontWeight: 400,
                                        color: "#aed6f1",
                                        flexShrink: 0,
                                    }}
                                >
                                    {Math.round(sh.probability * 100)}%
                                </span>
                                <span
                                    style={{
                                        backgroundColor: "#1a252f",
                                        borderRadius: "10px",
                                        fontSize: "0.75em",
                                        padding: '3px',
                                        fontWeight: 400,
                                        color: "#aed6f1",
                                        flexShrink: 0,
                                    }}
                                >
                                    {sh.opportunityIds.length} opp
                                    {sh.opportunityIds.length !== 1 ? "s" : ""}
                                </span>
                            </div>
                        )
                    }

                    const opp = data as OpportunityRow
                    return (
                        <>
                            <a
                                ref={(el) => el?.style.setProperty("text-decoration", "underline", "important")}
                                onClick={() => handleNavigateToAcumatica(opp)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    maxWidth: "260px",
                                }}
                            >
                                {opp.description || ""}
                            </a>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    fontSize: "0.75em",
                                    color: "var(--bs-secondary-color)",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            >
                                {opp.client?.name ?? ""}
                            </div>
                        </>
                    )
                },
            },

            // ---- Total column ----
            {
                header: "Value",
                id: "opp-total",
                size: 150,
                meta: {
                    getHeaderStyle: () => ({ textAlign: "right", padding: "8px 12px" }),
                    getCellStyle: (cell: any) => {
                        const data = cell.row.original as TableRow
                        if (data.__rowType === "stage-header") {
                            return {
                                padding: "7.5px 0px",
                                backgroundColor: getStageBackgroundColor((data as StageHeaderRow).stageName),
                            }
                        }
                        return {
                            padding: "0px 0px",
                            backgroundColor: "var(--bs-success-bg-subtle)"
                        }
                    },
                    getFooterStyle: () => ({ padding: 0, backgroundColor: "darkgreen" }),
                },
                footer: ({ table }) => {
                    if (table.getRowModel().rows.length === 0) return null
                    return (
                        <div style={{ padding: "10px 12px", }}>
                            <div
                                className="opp-total-summary"
                                style={{ ...stageHeaderTopStyle, color: 'white' }}
                            />
                            <div
                                title="Weighted"
                                className="opp-weighted-total-summary"
                                style={{ ...stageHeaderBotStyle, color: 'white' }}
                            />
                        </div>
                    )
                },
                cell: ({ row }) => {
                    const data = row.original

                    if (data.__rowType === "stage-header") {
                        const sh = data as StageHeaderRow
                        const stageOpps = filteredOpportunities.filter(
                            (o) => o.stage?.id === sh.stageId
                        )
                        const rawTotal = stageOpps.reduce(
                            (sum, o) => sum + Number(o.amount ?? 0),
                            0
                        )
                        const weightedTotal = rawTotal * sh.probability
                        return (
                            <div
                                style={{
                                    padding: "0px 12px"
                                }}
                            >
                                <div
                                    className="stage-value"
                                    style={{
                                        ...stageHeaderTopStyle,
                                        color: getStageHeaderTextColor(data.stageName),
                                    }}
                                >
                                    {formatCurrency(rawTotal)}
                                </div>
                                <div
                                    title={`Weighted Value (${sh.probability})`}
                                    className="stage-value-weighted"
                                    style={{ ...stageHeaderBotStyle }}
                                >
                                    {formatCurrency(weightedTotal)}
                                </div>
                            </div>
                        )
                    }

                    const opp = data as OpportunityRow
                    const prob = getStageProbability(opp.stage?.description ?? "")
                    const weighted = Number(opp.amount ?? 0) * prob
                    return (
                        <div
                        style={{
                            padding: "0px 12px"
                        }}
                        >
                            <div
                                className="opp-total-text"
                                style={{
                                    ...oppTopStyle,
                                    backgroundColor: "var(--bs-success-bg-subtle)",
                                    color: "var(--bs-success-text-emphasis)",
                                    fontWeight: 600,
                                }}
                            >
                                {formatCurrency(Number(opp.amount ?? 0))}
                            </div>
                            <div
                                title={`Weighted Value (${prob})`}
                                className="opp-weighted-total-text"
                                style={{
                                    ...oppBotStyle,

                                    color: "var(--bs-primary-text-emphasis)",
                                }}
                            >
                                {formatCurrency(weighted)}
                            </div>
                        </div>
                    )
                },
            },

            // ---- 12 Period columns ----
            ...[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((p) => buildPeriodCell(p)),

            // ---- Forecast Total column (last) ----
            {
                header: "Total",
                id: "forecast-total",
                size: 120,
                meta: {
                    getHeaderStyle: () => ({ textAlign: "right", padding: "8px 12px" }),
                    getCellStyle: (cell: any) => {
                        const data = cell.row.original as TableRow
                        if (data.__rowType === "stage-header") {
                            return {
                                padding: "0px 10px 0px 0px",
                                backgroundColor: getStageBackgroundColor((data as StageHeaderRow).stageName),
                            }
                        }
                        return { padding: "0px 10px 0px 0px" }
                    },
                    getFooterStyle: () => ({ padding: "0px 10px 0px 0px", backgroundColor: "darkgreen", }),
                },
                footer: ({ table }) => {
                    if (table.getRowModel().rows.length === 0) return null
                    return (
                        <div style={{ backgroundColor: "darkgreen", }}>
                            <div
                                className="forecast-total-grand-summary"
                                style={{
                                    ...stageHeaderTopStyle,
                                    color: "white"
                                }}
                            />
                            <div
                                className="forecast-total-grand-weighted-summary"
                                style={{ ...stageHeaderBotStyle, color: "white" }}
                            />
                        </div>
                    )
                },
                cell: ({ row }) => {
                    const data = row.original

                    if (data.__rowType === "stage-header") {
                        const sh = data as StageHeaderRow
                        // Initial values computed from base data (DOM will update on changes)
                        const stageOpps = filteredOpportunities.filter(
                            (o) => o.stage?.id === sh.stageId
                        )
                        const rawTotal = stageOpps.reduce(
                            (sum, o) => sum + computeOppForecastTotal(o, {}),
                            0
                        )
                        const weightedTotal = rawTotal * sh.probability
                        return (
                            <>
                                <div
                                    className={`stage-forecast-total-${sh.stageId}`}
                                    style={{
                                        ...stageHeaderTopStyle,
                                        color: getStageHeaderTextColor(data.stageName),
                                    }}
                                >
                                    {formatCurrency(rawTotal)}
                                </div>
                                <div
                                    title={`Weighted Value (${sh.probability})`}
                                    className={`stage-forecast-total-weighted-${sh.stageId}`}
                                    style={{ ...stageHeaderBotStyle }}
                                >
                                    {formatCurrency(weightedTotal)}
                                </div>
                            </>
                        )
                    }

                    const opp = data as OpportunityRow
                    const prob = getStageProbability(opp.stage?.description ?? "")
                    const raw = computeOppForecastTotal(opp, {})
                    const weighted = raw * prob

                    return (
                        <>
                            <div
                                className={`forecast-total-raw-${opp.id}`}
                                style={{
                                    ...oppTopStyle,
                                    fontWeight: 600,
                                }}
                            >
                                {formatCurrency(raw)}
                            </div>
                            <div
                                title={`Weighted Value (${prob})`}
                                className={`forecast-total-weighted-${opp.id}`}
                                style={{
                                    ...oppBotStyle,
                                }}
                            >
                                {formatCurrency(weighted)}
                            </div>
                        </>
                    )
                },
            },
        ],
        [fiscalYear, filteredOpportunities, collapsedStages]
    )

    // Rows are grouped under stage-header rows, so column sorting would break the grouping
    const table = useDataTable({
        data: tableRows,
        columns,
        enableSorting: false,
        getRowId: (row) =>
            row.__rowType === "stage-header"
                ? `stage-${(row as StageHeaderRow).stageId}`
                : (row as OpportunityRow).id,
        columnResizeMode: 'onEnd',
        enableColumnResizing: true
    })


    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------    
    return (
        <DataTableTemplate
            fullExport={handleExportData}
            title={"Opportunity"}
            breadcrumbItem={"Opportunity Forecast"}
            table={table}
            columns={columns}
            isLoading={isLoadingOpportunities}
            rowCount={filteredOpportunities.length}
            filters={
                <SearchOpportunitiesForm
                    handleClearSearch={handleClearSearch}
                    query={query}
                    handleChangeQuery={handleChangeQuery}
                    branches={branches.map((b: Branch) => ({ value: b.id, label: b.name }))}
                    projectManagers={projectManagers.map((emp: Employee) => ({
                        value: emp.id,
                        label: `${emp.commonName ?? emp.firstName} ${emp.lastName}`,
                    }))}
                    departments={toDepartmentOptions(departments)}
                    clients={clients.map((c: Client) => ({ value: c.id, label: c.name }))}
                    handleSearch={handleSearch}
                    isSearching={isLoadingOpportunities}
                />
            }

            actions={<>
                {
                    <ActionButton
                        onClick={handleOpenManual}
                    >
                        Help <HelpIcon />
                    </ActionButton>
                }
            </>}
            modal={<></>}
            tableActions={
                <>
                    <div id={FORECAST_CONTROLS_ID} style={{ display: "none" }}>
                        <PrimaryButton
                            style={{ minWidth: "100px" }}
                            onClick={handleSave}
                            disabled={isSavingForecast}
                        >
                            {isSavingForecast ? "Saving..." : "Save"}
                        </PrimaryButton>
                        <SecondaryButton style={{ marginLeft: "10px" }} onClick={handleReset}>
                            Reset
                        </SecondaryButton>
                    </div>

                    <SearchSelect
                        value={fiscalYear.toString()}
                        onChange={(e) =>
                            handleChangeFiscalYear(
                                e?.value ? parseFloat(e.value) : defaultYear
                            )
                        }
                        options={fiscalYearOptions}
                        inputStyle={{ margin: 0, minWidth: "150px" }}
                    />
                </>
            }
        />
    )
}

export default withRouter(OpportunityForecastPage)